import React, { useState, useEffect, useCallback } from 'react'
import { ArrowLeft, Plus, Pencil, Trash2, Star, AlertCircle, Loader2, User as UserIcon, Target, TrendingUp } from 'lucide-react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select'
import { KRScoringDialog } from '@/components/performance/Scoring/KRScoringDialog'
import { useEvaluationUIStore } from '@/stores/evaluation-ui-store'
import { useAuth } from '@/hooks/useAuth'
import { useTenant } from '@/hooks/useTenant'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import {
  getAllIndividualOKRs,
  listKeyResultsByOKR,
  createOKR,
  updateOKR,
  deleteOKR,
  createKeyResult,
  updateKeyResult,
  deleteKeyResult,
  updateKeyResultProgress,
} from '@/api/performance'
import type { KeyResult, OKR, CreateOKRData, CreateKeyResultData } from '@/types/performance'
import { toast } from 'sonner'

// Enriched OKR returned by the individual-all endpoint
interface EnrichedOKR extends OKR {
  ownerName?: string
  objectiveScore?: number | null
  totalKRs?: number
  scoredKRs?: number
  evaluators?: Array<{ name: string; score?: number }>
}

const ADMIN_ROLES = ['admin', 'rh', 'analista-rh', 'diretor-tecnico', 'coordenador']

const getInitials = (name: string) => {
  if (!name) return '?'
  const parts = name.split(' ').filter(p => p.length > 0)
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`
  if (parts.length === 1) return `${parts[0][0]}${parts[0][1] || ''}`
  return '?'
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  active: 'Ativo',
  completed: 'Concluído',
  cancelled: 'Cancelado',
  'not-started': 'Não Iniciado',
  'in-progress': 'Em Progresso',
  'at-risk': 'Em Risco',
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-500/20 text-gray-300',
  active: 'bg-emerald-500/20 text-emerald-400',
  completed: 'bg-blue-500/20 text-blue-400',
  cancelled: 'bg-red-500/20 text-red-400',
  'not-started': 'bg-gray-500/20 text-gray-300',
  'in-progress': 'bg-yellow-500/20 text-yellow-400',
  'at-risk': 'bg-orange-500/20 text-orange-400',
}

function OKRDetailPage() {
  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as { ownerId?: string; ownerName?: string }
  const ownerId = searchParams?.ownerId || null
  const ownerName = searchParams?.ownerName ? decodeURIComponent(searchParams.ownerName) : 'Colaborador'

  const { user } = useAuth()
  const { tenantDomain } = useTenant()
  const enterFocusMode = useEvaluationUIStore((s) => s.enterFocusMode)
  const exitFocusMode = useEvaluationUIStore((s) => s.exitFocusMode)

  const isAdmin = user?.role ? ADMIN_ROLES.includes(user.role) : false

  // Data state
  const [okrs, setOkrs] = useState<EnrichedOKR[]>([])
  const [krMap, setKrMap] = useState<Record<string, KeyResult[]>>({})
  const [loading, setLoading] = useState(true)
  const [loadingKRs, setLoadingKRs] = useState<Record<string, boolean>>({})

  // Create OKR dialog
  const [isCreateOKROpen, setIsCreateOKROpen] = useState(false)
  const [createOKRLoading, setCreateOKRLoading] = useState(false)
  const [objetivoOKR, setObjetivoOKR] = useState('')
  const [descricaoOKR, setDescricaoOKR] = useState('')
  const [categoriaOKR, setCategoriaOKR] = useState('')
  const [dataInicioOKR, setDataInicioOKR] = useState('')
  const [dataFimOKR, setDataFimOKR] = useState('')

  // Edit OKR dialog
  const [editingOKR, setEditingOKR] = useState<EnrichedOKR | null>(null)
  const [isEditOKROpen, setIsEditOKROpen] = useState(false)
  const [editOKRLoading, setEditOKRLoading] = useState(false)
  const [editObjetivo, setEditObjetivo] = useState('')
  const [editDescricao, setEditDescricao] = useState('')
  const [editCategoria, setEditCategoria] = useState('')
  const [editStatus, setEditStatus] = useState('')
  const [editDataInicio, setEditDataInicio] = useState('')
  const [editDataFim, setEditDataFim] = useState('')

  // Add KR dialog
  const [addKRForOKR, setAddKRForOKR] = useState<string | null>(null)
  const [isAddKROpen, setIsAddKROpen] = useState(false)
  const [addKRLoading, setAddKRLoading] = useState(false)
  const [nomeKR, setNomeKR] = useState('')
  const [descricaoKR, setDescricaoKR] = useState('')
  const [targetValueKR, setTargetValueKR] = useState('')
  const [unitKR, setUnitKR] = useState('')
  const [pesoKR, setPesoKR] = useState('')
  const [dataInicioKR, setDataInicioKR] = useState('')
  const [dataFimKR, setDataFimKR] = useState('')

  // Edit KR dialog
  const [editingKR, setEditingKR] = useState<KeyResult | null>(null)
  const [isEditKROpen, setIsEditKROpen] = useState(false)
  const [editKRLoading, setEditKRLoading] = useState(false)
  const [editNomeKR, setEditNomeKR] = useState('')
  const [editDescricaoKR, setEditDescricaoKR] = useState('')
  const [editTargetValueKR, setEditTargetValueKR] = useState('')
  const [editUnitKR, setEditUnitKR] = useState('')
  const [editPesoKR, setEditPesoKR] = useState('')
  const [editStatusKR, setEditStatusKR] = useState('')

  // KR Scoring dialog
  const [scoringKR, setScoringKR] = useState<KeyResult | null>(null)
  const [isScoringDialogOpen, setIsScoringDialogOpen] = useState(false)

  // Delete confirmation dialog
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'okr' | 'kr'; id: string; okrId?: string; name: string } | null>(null)

  // Progress update dialog
  const [progressKR, setProgressKR] = useState<KeyResult | null>(null)
  const [isProgressOpen, setIsProgressOpen] = useState(false)
  const [progressValue, setProgressValue] = useState('')
  const [progressLoading, setProgressLoading] = useState(false)

  // Enter focus mode on mount, exit on unmount
  useEffect(() => {
    enterFocusMode()
    return () => exitFocusMode()
  }, [enterFocusMode, exitFocusMode])

  // Load OKRs
  const loadOKRs = useCallback(async () => {
    if (!tenantDomain || !ownerId) return
    setLoading(true)
    try {
      const result = await getAllIndividualOKRs(tenantDomain, { search: ownerName, limit: 100 })
      const items = (result.items || []) as EnrichedOKR[]
      const filtered = items.filter((okr) => okr.ownerId === ownerId)
      setOkrs(filtered)

      // Load KRs for each OKR in parallel
      const krEntries = await Promise.all(
        filtered.map(async (okr) => {
          setLoadingKRs((prev) => ({ ...prev, [okr.okrId]: true }))
          try {
            const krResult = await listKeyResultsByOKR(tenantDomain, okr.okrId)
            return [okr.okrId, krResult.keyResults || []] as [string, KeyResult[]]
          } catch (err) {
            console.error(`Error loading KRs for OKR ${okr.okrId}:`, err)
            return [okr.okrId, [] as KeyResult[]] as [string, KeyResult[]]
          } finally {
            setLoadingKRs((prev) => ({ ...prev, [okr.okrId]: false }))
          }
        })
      )
      const newKrMap: Record<string, KeyResult[]> = {}
      for (const [okrId, krs] of krEntries) {
        newKrMap[okrId] = krs
      }
      setKrMap(newKrMap)
    } catch (err) {
      console.error('Error loading OKRs:', err)
      toast.error('Erro ao carregar objetivos')
    } finally {
      setLoading(false)
    }
  }, [tenantDomain, ownerId, ownerName])

  useEffect(() => {
    loadOKRs()
  }, [loadOKRs])

  // --- Handlers ---

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      navigate({ to: '/performance/objetivos-individuais' })
    }
  }

  // Create OKR
  const resetCreateOKRForm = () => {
    setObjetivoOKR('')
    setDescricaoOKR('')
    setCategoriaOKR('')
    setDataInicioOKR('')
    setDataFimOKR('')
  }

  const handleCreateOKR = async () => {
    if (!tenantDomain || !ownerId || !objetivoOKR || !dataInicioOKR || !dataFimOKR) {
      toast.error('Preencha todos os campos obrigatórios')
      return
    }
    setCreateOKRLoading(true)
    try {
      const newOKR = await createOKR(tenantDomain, {
        type: 'individual',
        ownerId,
        objective: objetivoOKR,
        description: descricaoOKR || undefined,
        category: categoriaOKR || undefined,
        period: { startDate: dataInicioOKR, endDate: dataFimOKR },
        status: 'active',
      })
      const enriched: EnrichedOKR = { ...newOKR, ownerName }
      setOkrs((prev) => [...prev, enriched])
      setKrMap((prev) => ({ ...prev, [newOKR.okrId]: [] }))
      resetCreateOKRForm()
      setIsCreateOKROpen(false)
      toast.success('Objetivo criado com sucesso')
    } catch (err) {
      console.error('Error creating OKR:', err)
      toast.error('Erro ao criar objetivo')
    } finally {
      setCreateOKRLoading(false)
    }
  }

  // Edit OKR
  const openEditOKR = (okr: EnrichedOKR) => {
    setEditingOKR(okr)
    setEditObjetivo(okr.objective)
    setEditDescricao(okr.description || '')
    setEditCategoria(okr.category || '')
    setEditStatus(okr.status)
    setEditDataInicio(okr.period?.startDate || '')
    setEditDataFim(okr.period?.endDate || '')
    setIsEditOKROpen(true)
  }

  const handleUpdateOKR = async () => {
    if (!tenantDomain || !editingOKR) return
    setEditOKRLoading(true)
    try {
      const updated = await updateOKR(tenantDomain, editingOKR.okrId, {
        objective: editObjetivo,
        description: editDescricao || undefined,
        category: editCategoria || undefined,
        status: editStatus as OKR['status'],
        period: editDataInicio && editDataFim ? { startDate: editDataInicio, endDate: editDataFim } : undefined,
      })
      setOkrs((prev) => prev.map((o) => (o.okrId === updated.okrId ? { ...o, ...updated } : o)))
      setIsEditOKROpen(false)
      setEditingOKR(null)
      toast.success('Objetivo atualizado com sucesso')
    } catch (err) {
      console.error('Error updating OKR:', err)
      toast.error('Erro ao atualizar objetivo')
    } finally {
      setEditOKRLoading(false)
    }
  }

  // Delete OKR — opens confirmation dialog
  const handleDeleteOKR = (okr: EnrichedOKR) => {
    setDeleteTarget({ type: 'okr', id: okr.okrId, name: okr.objective })
  }

  // Add KR
  const resetAddKRForm = () => {
    setNomeKR('')
    setDescricaoKR('')
    setTargetValueKR('')
    setUnitKR('')
    setPesoKR('')
    setDataInicioKR('')
    setDataFimKR('')
  }

  const openAddKR = (okrId: string) => {
    setAddKRForOKR(okrId)
    resetAddKRForm()
    setIsAddKROpen(true)
  }

  const handleCreateKR = async () => {
    if (!tenantDomain || !addKRForOKR || !nomeKR) {
      toast.error('Preencha o nome do Key Result')
      return
    }
    setAddKRLoading(true)
    try {
      const newKR = await createKeyResult(tenantDomain, addKRForOKR, {
        okrId: addKRForOKR,
        name: nomeKR,
        description: descricaoKR || undefined,
        targetValue: targetValueKR ? parseFloat(targetValueKR) : undefined,
        unit: unitKR || undefined,
        weight: pesoKR ? parseInt(pesoKR, 10) : undefined,
        ownerId: ownerId!,
        status: 'in-progress',
        period: dataInicioKR && dataFimKR ? { startDate: dataInicioKR, endDate: dataFimKR } : undefined,
      })
      setKrMap((prev) => ({
        ...prev,
        [addKRForOKR]: [...(prev[addKRForOKR] || []), newKR],
      }))
      resetAddKRForm()
      setIsAddKROpen(false)
      setAddKRForOKR(null)
      toast.success('Key Result criado com sucesso')
    } catch (err) {
      console.error('Error creating KR:', err)
      toast.error('Erro ao criar Key Result')
    } finally {
      setAddKRLoading(false)
    }
  }

  // Edit KR
  const openEditKR = (kr: KeyResult) => {
    setEditingKR(kr)
    setEditNomeKR(kr.name)
    setEditDescricaoKR(kr.description || '')
    setEditTargetValueKR(kr.targetValue?.toString() || '')
    setEditUnitKR(kr.unit || '')
    setEditPesoKR(kr.weight?.toString() || '')
    setEditStatusKR(kr.status)
    setIsEditKROpen(true)
  }

  const handleUpdateKR = async () => {
    if (!tenantDomain || !editingKR) return
    setEditKRLoading(true)
    try {
      const updated = await updateKeyResult(tenantDomain, editingKR.krId, {
        name: editNomeKR,
        description: editDescricaoKR || undefined,
        targetValue: editTargetValueKR ? parseFloat(editTargetValueKR) : undefined,
        unit: editUnitKR || undefined,
        weight: editPesoKR ? parseInt(editPesoKR, 10) : undefined,
        status: editStatusKR as KeyResult['status'],
      })
      setKrMap((prev) => ({
        ...prev,
        [editingKR.okrId]: (prev[editingKR.okrId] || []).map((kr) =>
          kr.krId === updated.krId ? updated : kr
        ),
      }))
      setIsEditKROpen(false)
      setEditingKR(null)
      toast.success('Key Result atualizado com sucesso')
    } catch (err) {
      console.error('Error updating KR:', err)
      toast.error('Erro ao atualizar Key Result')
    } finally {
      setEditKRLoading(false)
    }
  }

  // Delete KR — opens confirmation dialog
  const handleDeleteKR = (kr: KeyResult) => {
    setDeleteTarget({ type: 'kr', id: kr.krId, okrId: kr.okrId, name: kr.name })
  }

  // Confirm delete (shared handler for OKR and KR)
  const handleConfirmDelete = async () => {
    if (!tenantDomain || !deleteTarget) return
    try {
      if (deleteTarget.type === 'okr') {
        await deleteOKR(tenantDomain, deleteTarget.id)
        setOkrs((prev) => prev.filter((o) => o.okrId !== deleteTarget.id))
        setKrMap((prev) => {
          const next = { ...prev }
          delete next[deleteTarget.id]
          return next
        })
        toast.success('Objetivo excluído com sucesso')
      } else {
        await deleteKeyResult(tenantDomain, deleteTarget.id)
        setKrMap((prev) => ({
          ...prev,
          [deleteTarget.okrId!]: (prev[deleteTarget.okrId!] || []).filter((k) => k.krId !== deleteTarget.id),
        }))
        toast.success('Key Result excluído com sucesso')
      }
    } catch (err) {
      console.error('Error deleting:', err)
      toast.error(deleteTarget.type === 'okr' ? 'Erro ao excluir objetivo' : 'Erro ao excluir Key Result')
    } finally {
      setDeleteTarget(null)
    }
  }

  // Progress update dialog
  const openProgressDialog = (kr: KeyResult) => {
    setProgressKR(kr)
    setProgressValue(kr.currentValue?.toString() || '0')
    setIsProgressOpen(true)
  }

  const handleSaveProgress = async () => {
    if (!tenantDomain || !progressKR) return
    const newValue = parseFloat(progressValue)
    if (isNaN(newValue)) {
      toast.error('Valor inválido')
      return
    }
    setProgressLoading(true)
    try {
      const updated = await updateKeyResultProgress(tenantDomain, progressKR.krId, newValue)
      setKrMap((prev) => ({
        ...prev,
        [progressKR.okrId]: (prev[progressKR.okrId] || []).map((k) => k.krId === updated.krId ? updated : k),
      }))
      toast.success('Progresso atualizado')
      setIsProgressOpen(false)
      setProgressKR(null)
    } catch (err) {
      console.error('Error updating progress:', err)
      toast.error('Erro ao atualizar progresso')
    } finally {
      setProgressLoading(false)
    }
  }

  // Score KR
  const handleScoreKR = (kr: KeyResult) => {
    setScoringKR(kr)
    setIsScoringDialogOpen(true)
  }

  // --- Computed stats ---
  const totalOKRs = okrs.length
  const activeOKRs = okrs.filter((o) => o.status === 'active').length
  const totalKRs = Object.values(krMap).reduce((sum, krs) => sum + krs.length, 0)

  // --- Render helpers ---

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—'
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR')
    } catch {
      return dateStr
    }
  }

  const getProgressPercent = (current: number, target?: number | null) => {
    if (!target || target === 0) return 0
    return Math.min(100, Math.round((current / target) * 100))
  }

  // --- Guard: no ownerId ---
  if (!ownerId) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <Card className="p-12 text-center max-w-md mx-auto">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Colaborador não encontrado</h2>
          <p className="text-brand-gray mb-6">Nenhum colaborador foi selecionado.</p>
          <Button onClick={() => navigate({ to: '/performance/objetivos-individuais' })}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar aos Objetivos
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-brand-dark">
      {/* Top Bar */}
      <div className="sticky top-0 z-10 bg-brand-dark/95 backdrop-blur-sm border-b border-brand-gray/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-sm text-brand-gray hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar aos Objetivos
          </button>
          {isAdmin && (
            <Button
              size="sm"
              className="bg-success text-black hover:bg-success hover:opacity-90"
              onClick={() => setIsCreateOKROpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar Objetivo
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Employee Header */}
        <Card className="bg-brand-dark-light border-brand-gray/10 overflow-hidden">
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-5">
              {/* Gradient Avatar */}
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-primary to-brand-accent-600 flex items-center justify-center shrink-0 shadow-lg shadow-brand-primary/20">
                <span className="text-xl font-bold text-white uppercase">{getInitials(ownerName)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-display font-bold text-white truncate">{ownerName}</h1>
                <p className="text-brand-gray text-sm mt-0.5">Objetivos Individuais (OKRs)</p>
              </div>
            </div>
            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-brand-gray/10">
              <div className="bg-brand-dark rounded-lg p-4 text-center">
                <p className="text-3xl font-display font-bold text-white">{totalOKRs}</p>
                <p className="text-xs text-brand-gray mt-1 uppercase tracking-wide">Objetivos</p>
              </div>
              <div className="bg-brand-dark rounded-lg p-4 text-center">
                <p className="text-3xl font-display font-bold text-emerald-400">{activeOKRs}</p>
                <p className="text-xs text-brand-gray mt-1 uppercase tracking-wide">Ativos</p>
              </div>
              <div className="bg-brand-dark rounded-lg p-4 text-center">
                <p className="text-3xl font-display font-bold text-blue-400">{totalKRs}</p>
                <p className="text-xs text-brand-gray mt-1 uppercase tracking-wide">Key Results</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Loading state */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-brand-primary animate-spin mb-3" />
            <span className="text-brand-gray text-sm">Carregando objetivos...</span>
          </div>
        )}

        {/* Empty state */}
        {!loading && okrs.length === 0 && (
          <Card className="bg-brand-dark-light border-brand-gray/10 p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-brand-dark flex items-center justify-center mx-auto mb-5">
              <Target className="w-8 h-8 text-brand-gray" />
            </div>
            <h3 className="text-lg font-display font-semibold text-white mb-2">Nenhum objetivo encontrado</h3>
            <p className="text-brand-gray text-sm mb-8 max-w-sm mx-auto">
              Este colaborador ainda não possui objetivos cadastrados.
            </p>
            {isAdmin && (
              <Button
                className="bg-success text-black hover:bg-success hover:opacity-90"
                onClick={() => setIsCreateOKROpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Objetivo
              </Button>
            )}
          </Card>
        )}

        {/* OKR Cards */}
        {!loading &&
          okrs.map((okr, okrIndex) => {
            const krs = krMap[okr.okrId] || []
            const isLoadingKRs = loadingKRs[okr.okrId]
            const overallProgress = okr.progress != null ? Math.min(100, Math.round(okr.progress)) : null
            return (
              <Card key={okr.okrId} className="bg-brand-dark-light border-brand-gray/10 overflow-hidden">
                {/* Color accent strip at top */}
                <div className="h-1 bg-gradient-to-r from-brand-primary to-brand-accent-600" />

                {/* OKR Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Objective number + title */}
                      <div className="flex items-start gap-3 mb-3">
                        <span className="shrink-0 w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary text-sm font-bold flex items-center justify-center mt-0.5">
                          {okrIndex + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-display font-semibold text-white leading-snug">{okr.objective}</h3>
                          {okr.description && (
                            <p className="text-sm text-brand-gray mt-1.5 leading-relaxed">{okr.description}</p>
                          )}
                        </div>
                      </div>

                      {/* Metadata row */}
                      <div className="flex flex-wrap items-center gap-2 ml-11">
                        <Badge className={`text-xs ${STATUS_COLORS[okr.status] || 'bg-gray-500/20 text-gray-300'}`}>
                          {STATUS_LABELS[okr.status] || okr.status}
                        </Badge>
                        {okr.category && (
                          <Badge variant="outline" className="text-xs border-brand-gray/20 text-brand-gray">
                            {okr.category}
                          </Badge>
                        )}
                        <span className="text-xs text-brand-gray/60">
                          {formatDate(okr.period?.startDate)} — {formatDate(okr.period?.endDate)}
                        </span>
                        {okr.objectiveScore != null && (
                          <Badge className="bg-yellow-500/15 text-yellow-400 text-xs">
                            Score: {typeof okr.objectiveScore === 'number' ? okr.objectiveScore.toFixed(1) : okr.objectiveScore}
                          </Badge>
                        )}
                      </div>

                      {/* Evaluators */}
                      {okr.evaluators && okr.evaluators.length > 0 && (
                        <div className="flex items-center gap-2 mt-3 ml-11 text-xs text-brand-gray">
                          <span className="text-brand-gray/60">Avaliadores:</span>
                          {okr.evaluators.map((ev, i) => (
                            <Badge key={i} variant="outline" className="text-xs border-brand-gray/20 text-brand-gray">
                              {ev.name}
                              {ev.score != null && ` (${ev.score})`}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Right side: progress + actions */}
                    <div className="flex flex-col items-end gap-3 shrink-0">
                      {overallProgress != null && (
                        <div className="flex items-center gap-3">
                          <div className="w-28 h-2.5 bg-brand-gray/20 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                              style={{ width: `${overallProgress}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-white w-11 text-right">{overallProgress}%</span>
                        </div>
                      )}
                      {isAdmin && (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => openEditOKR(okr)}
                            className="inline-flex items-center justify-center rounded-monumenta h-8 w-8 text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                            title="Editar objetivo"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteOKR(okr)}
                            className="inline-flex items-center justify-center rounded-monumenta h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-400/10 transition-colors"
                            title="Excluir objetivo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* KR Section */}
                <div className="border-t border-brand-gray/10 bg-brand-dark/40">
                  {/* KR Header */}
                  <div className="px-6 py-3 flex items-center justify-between">
                    <h4 className="text-xs font-medium text-brand-gray uppercase tracking-wider flex items-center gap-1.5">
                      <TrendingUp className="w-3.5 h-3.5" />
                      Key Results ({krs.length})
                    </h4>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => openAddKR(okr.okrId)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-400 hover:text-emerald-300 transition-colors px-2.5 py-1.5 rounded-lg hover:bg-emerald-400/10"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Adicionar KR
                      </button>
                    )}
                  </div>

                  {/* KR Loading */}
                  {isLoadingKRs && (
                    <div className="flex items-center gap-2 py-8 justify-center text-brand-gray text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Carregando Key Results...
                    </div>
                  )}

                  {/* KR Empty state */}
                  {!isLoadingKRs && krs.length === 0 && (
                    <div className="py-10 text-center">
                      <div className="w-10 h-10 rounded-full bg-brand-gray/10 flex items-center justify-center mx-auto mb-3">
                        <Target className="w-5 h-5 text-brand-gray/40" />
                      </div>
                      <p className="text-sm text-brand-gray/60 mb-4">Nenhum Key Result cadastrado</p>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => openAddKR(okr.okrId)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-400 hover:text-emerald-300 transition-colors px-4 py-2 rounded-lg bg-emerald-400/10 hover:bg-emerald-400/15"
                        >
                          <Plus className="w-4 h-4" />
                          Adicionar primeiro Key Result
                        </button>
                      )}
                    </div>
                  )}

                  {/* KR Cards */}
                  {!isLoadingKRs && krs.length > 0 && (
                    <div className="px-4 pb-4 space-y-2">
                      {krs.map((kr) => {
                        const progress = getProgressPercent(kr.currentValue, kr.targetValue)
                        return (
                          <div
                            key={kr.krId}
                            className="rounded-lg bg-brand-dark-light border border-brand-gray/10 p-4 hover:border-brand-gray/20 transition-colors group"
                          >
                            {/* KR Top: name + status + actions */}
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium text-white">{kr.name}</span>
                                  <Badge
                                    className={`text-[10px] px-1.5 py-0 leading-tight ${STATUS_COLORS[kr.status] || 'bg-gray-500/20 text-gray-300'}`}
                                  >
                                    {STATUS_LABELS[kr.status] || kr.status}
                                  </Badge>
                                  {kr.weight != null && kr.weight !== 100 && (
                                    <span className="text-[10px] text-brand-gray/60 bg-brand-gray/10 rounded px-1.5 py-0.5">
                                      Peso {kr.weight}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {/* KR Actions — always visible */}
                              <div className="flex items-center gap-0.5 shrink-0">
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleScoreKR(kr) }}
                                  className="inline-flex items-center justify-center rounded-monumenta h-8 w-8 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-400/10 transition-colors"
                                  title="Pontuar KR"
                                >
                                  <Star className="w-4 h-4" />
                                </button>
                                {isAdmin && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); openEditKR(kr) }}
                                      className="inline-flex items-center justify-center rounded-monumenta h-8 w-8 text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                                      title="Editar KR"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleDeleteKR(kr) }}
                                      className="inline-flex items-center justify-center rounded-monumenta h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-400/10 transition-colors"
                                      title="Excluir KR"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* KR Bottom: progress bar + metrics */}
                            <div className="flex items-center gap-4">
                              {/* Progress bar — prominent */}
                              <div className="flex-1 h-2 bg-brand-gray/20 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                              {/* Progress number */}
                              <span className="text-xs font-medium text-brand-gray shrink-0 w-9 text-right">
                                {progress}%
                              </span>
                              {/* Value display */}
                              <span className="text-xs text-brand-gray shrink-0 inline-flex items-center gap-1">
                                <span className="text-white font-medium">{kr.currentValue}</span>
                                {kr.targetValue != null && (
                                  <>
                                    <span className="text-brand-gray/60"> / </span>
                                    <span>{kr.targetValue}</span>
                                  </>
                                )}
                                {kr.unit && <span className="text-brand-gray/60 ml-0.5">{kr.unit}</span>}
                              </span>
                              {/* Update progress CTA */}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openProgressDialog(kr) }}
                                className="text-[11px] text-brand-primary hover:text-white shrink-0 transition-colors"
                              >
                                Atualizar progresso
                              </button>
                              {/* Score if available */}
                              {(kr as KeyResult & { averageScore?: number }).averageScore != null && (
                                <span className="text-xs text-yellow-400 bg-yellow-400/10 rounded px-1.5 py-0.5 shrink-0">
                                  {((kr as KeyResult & { averageScore?: number }).averageScore as number).toFixed(1)}
                                </span>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </Card>
            )
          })}
      </div>

      {/* ====== DIALOGS ====== */}

      {/* Create OKR Dialog */}
      <Dialog open={isCreateOKROpen} onOpenChange={setIsCreateOKROpen}>
        <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Criar Novo Objetivo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-brand-gray">Objetivo *</Label>
              <Textarea
                value={objetivoOKR}
                onChange={(e) => setObjetivoOKR(e.target.value)}
                placeholder="Descreva o objetivo..."
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-brand-gray">Descrição</Label>
              <Textarea
                value={descricaoOKR}
                onChange={(e) => setDescricaoOKR(e.target.value)}
                placeholder="Descrição detalhada (opcional)"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-brand-gray">Categoria</Label>
              <Input
                value={categoriaOKR}
                onChange={(e) => setCategoriaOKR(e.target.value)}
                placeholder="Ex: Gestão de Eventos"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-brand-gray">Data Início *</Label>
                <Input
                  type="date"
                  value={dataInicioOKR}
                  onChange={(e) => setDataInicioOKR(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-brand-gray">Data Fim *</Label>
                <Input
                  type="date"
                  value={dataFimOKR}
                  onChange={(e) => setDataFimOKR(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setIsCreateOKROpen(false)} disabled={createOKRLoading}>
                Cancelar
              </Button>
              <Button
                className="bg-success text-black hover:bg-success hover:opacity-90"
                onClick={handleCreateOKR}
                disabled={createOKRLoading || !objetivoOKR || !dataInicioOKR || !dataFimOKR}
              >
                {createOKRLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Criar Objetivo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit OKR Dialog */}
      <Dialog open={isEditOKROpen} onOpenChange={setIsEditOKROpen}>
        <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Objetivo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-brand-gray">Objetivo</Label>
              <Textarea
                value={editObjetivo}
                onChange={(e) => setEditObjetivo(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-brand-gray">Descrição</Label>
              <Textarea
                value={editDescricao}
                onChange={(e) => setEditDescricao(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-brand-gray">Categoria</Label>
              <Input
                value={editCategoria}
                onChange={(e) => setEditCategoria(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-brand-gray">Status</Label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Rascunho</SelectItem>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="completed">Concluído</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-brand-gray">Data Início</Label>
                <Input
                  type="date"
                  value={editDataInicio}
                  onChange={(e) => setEditDataInicio(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-brand-gray">Data Fim</Label>
                <Input
                  type="date"
                  value={editDataFim}
                  onChange={(e) => setEditDataFim(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setIsEditOKROpen(false)} disabled={editOKRLoading}>
                Cancelar
              </Button>
              <Button
                className="bg-success text-black hover:bg-success hover:opacity-90"
                onClick={handleUpdateOKR}
                disabled={editOKRLoading}
              >
                {editOKRLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Salvar Alterações
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add KR Dialog */}
      <Dialog open={isAddKROpen} onOpenChange={setIsAddKROpen}>
        <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Adicionar Key Result</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-brand-gray">Nome *</Label>
              <Input
                value={nomeKR}
                onChange={(e) => setNomeKR(e.target.value)}
                placeholder="Nome do Key Result"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-brand-gray">Descrição</Label>
              <Textarea
                value={descricaoKR}
                onChange={(e) => setDescricaoKR(e.target.value)}
                placeholder="Descrição (opcional)"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-brand-gray">Meta</Label>
                <Input
                  type="number"
                  value={targetValueKR}
                  onChange={(e) => setTargetValueKR(e.target.value)}
                  placeholder="100"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-brand-gray">Unidade</Label>
                <Input
                  value={unitKR}
                  onChange={(e) => setUnitKR(e.target.value)}
                  placeholder="%, R$, etc."
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-brand-gray">Peso</Label>
                <Input
                  type="number"
                  value={pesoKR}
                  onChange={(e) => setPesoKR(e.target.value)}
                  placeholder="100"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-brand-gray">Data Início</Label>
                <Input
                  type="date"
                  value={dataInicioKR}
                  onChange={(e) => setDataInicioKR(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-brand-gray">Data Fim</Label>
                <Input
                  type="date"
                  value={dataFimKR}
                  onChange={(e) => setDataFimKR(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setIsAddKROpen(false)} disabled={addKRLoading}>
                Cancelar
              </Button>
              <Button
                className="bg-success text-black hover:bg-success hover:opacity-90"
                onClick={handleCreateKR}
                disabled={addKRLoading || !nomeKR}
              >
                {addKRLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Adicionar KR
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit KR Dialog */}
      <Dialog open={isEditKROpen} onOpenChange={setIsEditKROpen}>
        <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Key Result</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-brand-gray">Nome</Label>
              <Input
                value={editNomeKR}
                onChange={(e) => setEditNomeKR(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-brand-gray">Descrição</Label>
              <Textarea
                value={editDescricaoKR}
                onChange={(e) => setEditDescricaoKR(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-brand-gray">Meta</Label>
                <Input
                  type="number"
                  value={editTargetValueKR}
                  onChange={(e) => setEditTargetValueKR(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-brand-gray">Unidade</Label>
                <Input
                  value={editUnitKR}
                  onChange={(e) => setEditUnitKR(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-brand-gray">Peso</Label>
                <Input
                  type="number"
                  value={editPesoKR}
                  onChange={(e) => setEditPesoKR(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-brand-gray">Status</Label>
              <Select value={editStatusKR} onValueChange={setEditStatusKR}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not-started">Não Iniciado</SelectItem>
                  <SelectItem value="in-progress">Em Progresso</SelectItem>
                  <SelectItem value="completed">Concluído</SelectItem>
                  <SelectItem value="at-risk">Em Risco</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setIsEditKROpen(false)} disabled={editKRLoading}>
                Cancelar
              </Button>
              <Button
                className="bg-success text-black hover:bg-success hover:opacity-90"
                onClick={handleUpdateKR}
                disabled={editKRLoading}
              >
                {editKRLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Salvar Alterações
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Progress Update Dialog */}
      <Dialog open={isProgressOpen} onOpenChange={(open) => { if (!open) { setIsProgressOpen(false); setProgressKR(null) } }}>
        <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Atualizar Progresso</DialogTitle>
          </DialogHeader>
          {progressKR && (
            <div className="space-y-5 mt-2">
              {/* KR info */}
              <div className="bg-brand-dark rounded-lg p-4">
                <p className="text-sm font-medium text-white mb-2">{progressKR.name}</p>
                <div className="flex items-center gap-4 text-xs text-brand-gray">
                  {progressKR.targetValue != null && (
                    <span>Meta: <span className="text-white">{progressKR.targetValue}{progressKR.unit ? ` ${progressKR.unit}` : ''}</span></span>
                  )}
                  <span>Atual: <span className="text-white">{progressKR.currentValue}{progressKR.unit ? ` ${progressKR.unit}` : ''}</span></span>
                </div>
                {/* Visual progress */}
                {progressKR.targetValue != null && progressKR.targetValue > 0 && (
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex-1 h-2 bg-brand-gray/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(100, Math.round((progressKR.currentValue / progressKR.targetValue) * 100))}%` }}
                      />
                    </div>
                    <span className="text-xs text-brand-gray w-9 text-right">
                      {Math.min(100, Math.round((progressKR.currentValue / progressKR.targetValue) * 100))}%
                    </span>
                  </div>
                )}
              </div>

              {/* Input */}
              <div>
                <Label className="text-brand-gray">Novo valor{progressKR.unit ? ` (${progressKR.unit})` : ''}</Label>
                <Input
                  type="number"
                  autoFocus
                  value={progressValue}
                  onChange={(e) => setProgressValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveProgress() }}
                  placeholder={progressKR.currentValue?.toString() || '0'}
                  className="mt-1"
                />
              </div>

              {/* Preview — what the progress will look like */}
              {progressKR.targetValue != null && progressKR.targetValue > 0 && progressValue && (
                <div className="flex items-center gap-3 text-xs text-brand-gray">
                  <span>Novo progresso:</span>
                  <div className="flex-1 h-2 bg-brand-gray/20 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-primary rounded-full transition-all duration-300"
                      style={{ width: `${Math.min(100, Math.round((parseFloat(progressValue) / progressKR.targetValue) * 100))}%` }}
                    />
                  </div>
                  <span className="text-white font-medium w-9 text-right">
                    {Math.min(100, Math.round((parseFloat(progressValue) / progressKR.targetValue) * 100))}%
                  </span>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => { setIsProgressOpen(false); setProgressKR(null) }} disabled={progressLoading}>
                  Cancelar
                </Button>
                <Button
                  className="bg-success text-black hover:bg-success hover:opacity-90"
                  onClick={handleSaveProgress}
                  disabled={progressLoading || !progressValue}
                >
                  {progressLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Salvar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* KR Scoring Dialog */}
      {isScoringDialogOpen && scoringKR && (
        <KRScoringDialog
          krId={scoringKR.krId}
          krName={scoringKR.name}
          isOpen={isScoringDialogOpen}
          onClose={() => {
            setIsScoringDialogOpen(false)
            setScoringKR(null)
          }}
          onScoreSubmitted={() => {
            // Reload KRs for this OKR to get updated score
            if (tenantDomain && scoringKR) {
              listKeyResultsByOKR(tenantDomain, scoringKR.okrId).then((res) => {
                setKrMap((prev) => ({
                  ...prev,
                  [scoringKR.okrId]: res.keyResults || [],
                }))
              })
            }
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>
              {deleteTarget?.type === 'okr' ? 'Excluir Objetivo' : 'Excluir Key Result'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-brand-gray">
              Tem certeza que deseja excluir{' '}
              <span className="text-white font-medium">{deleteTarget?.name}</span>?
              Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
                Cancelar
              </Button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  )
}

export default withPageProtection(OKRDetailPage, PAGES.PERFORMANCE_OBJETIVOS_INDIVIDUAIS)
